<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">

      <Form @submit="editRuleset">

        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Code <span class="text-danger">*</span>
              </label>
              <Field name="code" type="text" class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.code" placeholder="Entrer le code" />
              <ErrorMessage name="code" class="text-danger" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Libellé <span class="text-danger">*</span>
              </label>
              <Field name="name" type="text" class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.name" placeholder="Entrer le libellé" />
              <ErrorMessage name="name" class="text-danger" />
            </div>
          </div>

          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Description <span class="text-danger">*</span>
              </label>
              <Field name="description" as="textarea" class="form-control shadow-none fs-md-15 text-black" rows="4"
                v-model="formData.description" placeholder="Description..." />
              <ErrorMessage name="description" class="text-danger" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type de périmètre ciblé <span class="text-danger">*</span>
              </label>
              <Field name="scope_type" type="text" class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.scope_type" placeholder="Entrer le type de périmètre" />
              <ErrorMessage name="scope_type" class="text-danger" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Pays
              </label>
              <Multiselect :options="paysOptions" :searchable="true" track-by="value" label="label" :object="true"
                v-model="formData.pays" placeholder="Sélectionner le pays" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Port
              </label>
              <Multiselect :options="ports" :searchable="true" v-model="formData.port" :object="true"
                :loading="loadingPorts" placeholder="Rechercher (nomPort ou locode)..." label="label" track-by="value"
                @search-change="searchPort" :filter-results="false" :min-chars="2" />
              <ErrorMessage name="port" class="text-danger" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date d’entrée en vigueur
              </label>
              <Field name="effective_from" type="date" v-model="formData.effective_from"
                class="form-control shadow-none fs-md-15 text-black" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de fin d’application
              </label>
              <Field name="effective_to" v-model="formData.effective_to" type="date"
                class="form-control shadow-none fs-md-15 text-black" />
            </div>
          </div>

          <div class="col-md-12 mt-4">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit">
                Modifier le jeu de règles
              </button>
              <router-link to="/listeRuleSet" class="btn btn-danger transition border-0 lh-1 fw-medium">
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i> Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, nextTick, reactive } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { error, success } from '@/utils/utils';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "EditRuleSet",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // const rulesetId = computed(() => route.params.id as string);
    const rulesetId = route.params.id;
    interface Option {
      value: string;
      label: string;
    }
    const ports = ref<Option[]>([]);
    const paysOptions = ref<Option[]>([]);

    const formData = reactive({
      code: '',
      name: '',
      description: '',
      scope_type: '',
      effective_from: '',
      effective_to: '',
      pays: null as { value: string; label: string } | null, // Typage explicite
      port: null as { value: string; label: string } | null, // Typage explicite

    });

    const formatDate = (dateString: string | null): string => {
      if (!dateString) return '';
      return dateString.split('T')[0];
    };
    const loadingPorts = ref(false);
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchPays = async () => {
      try {
        const response = await ApiService.get('/pays');
        const paysData = response.data.data.data;
        paysOptions.value = paysData.map((p: any) => ({
          value: p.id,
          label: p.name_fr,
        }));
      } catch (err) {
        console.error(err);
      }
    };

    const searchPort = async (query: string) => {
      if (searchTimeout) clearTimeout(searchTimeout);

      if (!query || query.length < 2) {
        ports.value = [];
        return;
      }

      searchTimeout = setTimeout(async () => {
        loadingPorts.value = true;
        try {
          const response = await ApiService.get(`/Liste/port?search=${encodeURIComponent(query)}`);
          const portsData = response.data?.data || [];

          ports.value = portsData.map((port: any) => ({
            label: `${port.nomPort} - ${port.locode}`,
            value: port.id,
          }));
        } catch (err) {
          console.error('Erreur recherche ports:', err);
          ports.value = [];
        } finally {
          loadingPorts.value = false;
        }
      }, 300);
    };

    const getRuleset = async (id: string) => {
      try {
        const response = await ApiService.get(`/ruleset/${id}`);
        const ruleSet = response.data.data;
        formData.code = ruleSet.code || '';
        formData.name = ruleSet.name || '';
        formData.description = ruleSet.description || '';
        formData.scope_type = ruleSet.scope_type || '';
        formData.effective_from = formatDate(ruleSet.effective_from);
        formData.effective_to = formatDate(ruleSet.effective_to);
        formData.port = ruleSet.port ? { value: ruleSet.port.id, label: ruleSet.port.nomPort } : null;
        formData.pays = ruleSet.pays ? {
          value: ruleSet.pays.id,
          label: `${ruleSet.pays.name_fr}`
        } : null;
        await nextTick();
      } catch (err: any) {
        error("Impossible de charger le jeu de règles");
        console.error(err);
      }
    };

    // Soumission modification
    const editRuleset = async () => {
      try {
        const payload = {
          code: formData.code,
          name: formData.name,
          description: formData.description,
          scope_type: formData.scope_type,
          effective_from: formData.effective_from || null,
          effective_to: formData.effective_to || null,
          pays: formData.pays ? formData.pays.value : null,
          port: formData.port ? formData.port.value : null,
        };
        await ApiService.put(`/ruleset/${rulesetId}`, payload);
        success('Règle modifiée avec succès');
        router.push('/listeRuleSet');
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la modification");
      }
    };

    onMounted(async () => {
      fetchPays();
      if (rulesetId) await getRuleset(rulesetId as string);
    });

    return {
      editRuleset,
      paysOptions,
      ports,
      loadingPorts,
      searchPort,
      formData
    };
  },
});
</script>